/**
 * `useHarnessIndex` — react-query-backed Map<harnessId, manifest summary>
 * for any consumer that needs to look up a single harness's metadata by
 * id (description, label, tags, model). Cached for 60s like
 * `useHarnessCatalogue` so multiple consumers share the fetch.
 *
 * Used by the Lexiun Agents detail dialogs to surface the harness's
 * human-readable `tool.description` next to its id — the operator
 * pointed out the raw harness id alone is meaningless to a lawyer.
 *
 * Returns an empty Map while loading or on error (callers fall back to
 * showing just the id, which is acceptable graceful degradation).
 *
 * **Important — uses the lexserver-proxy path `/api/harn` directly
 * instead of the `useHarnClient` default.** The dev-only base
 * `/harn-gateway-base/harn` returns 500 on the catalog endpoint
 * (2026-06-23 diagnostic: direct probe in chrome). `/api/harn` works
 * in both dev (vite proxies `/api/*` to lexserver:3200) and prod (the
 * production gateway path). Same gateway under the hood, just a
 * different vite-side surface.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { HarnClient } from "@/lib/harn-client";
import { getToken } from "@/lib/lexiun-jwt";
import type { HarnessManifestSummary } from "@/types/harn";

const CATALOG_CLIENT = new HarnClient({
  baseUrl: "/api/harn",
  getToken: async () => getToken(),
  mode: "gateway",
});

export function useHarnessIndex(): Map<string, HarnessManifestSummary> {
  const query = useQuery({
    queryKey: ["harn", "catalog-index"],
    queryFn: async () => (await CATALOG_CLIENT.listHarnesses()).harnesses,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const map = new Map<string, HarnessManifestSummary>();
    (query.data ?? []).forEach((h) => map.set(h.id, h));
    return map;
  }, [query.data]);
}
